/**
 * ChatPanel - Full-featured AI Chat UI Component
 * 
 * Features:
 * - Scrollable message transcript with bubbles and timestamps
 * - Plan/Reasoning Summary (collapsible, safe summary only)
 * - File attachments with chips display
 * - Voice input (Web Speech API)
 * - Clear chat button
 * - Working state indicator
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import {
    Send, Paperclip, X, Mic, MicOff, Loader2, Trash2,
    ChevronDown, ChevronUp, Bot, User, Sparkles, FileText, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../utils/api';

// Voice input hook using Web Speech API
const useVoiceInput = () => {
    const [isListening, setIsListening] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [isSupported, setIsSupported] = useState(false);
    const recognitionRef = useRef(null);

    useEffect(() => {
        // Check for Web Speech API support
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (SpeechRecognition) {
            setIsSupported(true);
            const recognition = new SpeechRecognition();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = 'en-US';

            recognition.onresult = (event) => {
                let finalTranscript = '';
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const result = event.results[i];
                    if (result.isFinal) {
                        finalTranscript += result[0].transcript;
                    }
                }
                if (finalTranscript) {
                    setTranscript(prev => prev + finalTranscript);
                }
            };

            recognition.onerror = (event) => {
                console.error('Speech recognition error:', event.error);
                setIsListening(false);
                if (event.error === 'not-allowed') {
                    toast.error('Microphone access denied. Please allow microphone access.');
                }
            };

            recognition.onend = () => {
                setIsListening(false);
            };

            recognitionRef.current = recognition;
        }

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.abort();
            }
        };
    }, []);

    const startListening = useCallback(() => {
        if (!recognitionRef.current) return;
        setTranscript('');
        recognitionRef.current.start();
        setIsListening(true);
    }, []);

    const stopListening = useCallback(() => {
        if (!recognitionRef.current) return;
        recognitionRef.current.stop();
        setIsListening(false);
    }, []);

    const toggleListening = useCallback(() => {
        if (isListening) {
            stopListening();
        } else {
            startListening();
        }
    }, [isListening, startListening, stopListening]);

    const clearTranscript = useCallback(() => {
        setTranscript('');
    }, []);

    return {
        isListening,
        transcript,
        isSupported,
        toggleListening,
        clearTranscript
    };
};

// Format timestamp for display
const formatTimestamp = (date) => {
    return new Date(date).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    });
};

// Format file size for display
const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ChatPanel = ({
    projectId = null,
    conversationId: initialConversationId = null,
    onClose = null,
    className = '',
    compact = false
}) => {
    const [messages, setMessages] = useState([
        {
            id: 'welcome',
            role: 'assistant',
            content: "Hello! I'm your Digital Ninja AI assistant. How can I help you build your app today?",
            timestamp: new Date().toISOString(),
            planSummary: null
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [conversationId, setConversationId] = useState(initialConversationId);
    const [attachments, setAttachments] = useState([]); // [{file, name, size}]
    const [expandedSummaries, setExpandedSummaries] = useState(new Set());

    const scrollRef = useRef(null);
    const fileInputRef = useRef(null);
    const inputRef = useRef(null);

    const voice = useVoiceInput();

    // Auto-scroll to bottom on new messages
    useEffect(() => {
        if (scrollRef.current) {
            const scrollContainer = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        }
    }, [messages]);

    // Apply voice transcript to input
    useEffect(() => {
        if (voice.transcript) {
            setInputValue(prev => prev + voice.transcript);
            voice.clearTranscript();
        }
    }, [voice.transcript, voice.clearTranscript]);

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files || []);
        const newAttachments = files.map(file => ({
            file,
            name: file.name,
            size: file.size
        }));
        setAttachments(prev => [...prev, ...newAttachments]);
        // Clear the input so same file can be selected again
        e.target.value = '';
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const clearChat = () => {
        setMessages([{
            id: 'welcome-' + Date.now(),
            role: 'assistant',
            content: "Chat cleared. How can I help you?",
            timestamp: new Date().toISOString(),
            planSummary: null
        }]);
        setConversationId(null);
        setAttachments([]);
        toast.success('Chat cleared');
    };

    const toggleSummary = (messageId) => {
        setExpandedSummaries(prev => {
            const next = new Set(prev);
            if (next.has(messageId)) {
                next.delete(messageId);
            } else {
                next.add(messageId);
            }
            return next;
        });
    };

    const sendMessage = async () => {
        const trimmedInput = inputValue.trim();
        if (!trimmedInput && attachments.length === 0) return;
        if (isLoading) return;

        // Stop voice if listening
        if (voice.isListening) {
            voice.toggleListening();
        }

        // Add user message
        const userMessage = {
            id: 'user-' + Date.now(),
            role: 'user',
            content: trimmedInput,
            timestamp: new Date().toISOString(),
            attachments: attachments.map(a => ({ name: a.name, size: a.size }))
        };
        setMessages(prev => [...prev, userMessage]);

        const currentAttachments = [...attachments];
        setInputValue('');
        setAttachments([]);
        setIsLoading(true);

        try {
            const token = localStorage.getItem('token');

            // Build history for API (exclude welcome messages)
            const history = messages
                .filter(m => m.role !== 'system' && !m.id.startsWith('welcome'))
                .map(m => ({ role: m.role, content: m.content }));

            let response;

            if (currentAttachments.length > 0) {
                // Use multipart/form-data for attachments
                const formData = new FormData();
                formData.append('message', trimmedInput || 'Please analyze the attached files.');
                if (projectId) formData.append('project_id', projectId);
                if (conversationId) formData.append('conversation_id', conversationId);
                formData.append('history', JSON.stringify(history));

                currentAttachments.forEach(att => {
                    formData.append('files', att.file);
                });

                response = await fetch(`${BACKEND_URL}/api/chat/with-attachments`, {
                    method: 'POST',
                    headers: {
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    },
                    body: formData
                });
            } else {
                // Use JSON for simple messages
                response = await fetch(`${BACKEND_URL}/api/chat/enhanced`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                    },
                    body: JSON.stringify({
                        message: trimmedInput,
                        project_id: projectId,
                        conversation_id: conversationId,
                        history
                    })
                });
            }

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || `Request failed: ${response.status}`);
            }

            const data = await response.json();

            // Update conversation ID
            if (data.conversation_id) {
                setConversationId(data.conversation_id);
            }

            // Add assistant message
            const assistantMessage = {
                id: 'assistant-' + Date.now(),
                role: 'assistant',
                content: data.reply,
                timestamp: new Date().toISOString(),
                planSummary: data.plan_summary || null,
                attachmentsReceived: data.attachments_received || []
            };
            setMessages(prev => [...prev, assistantMessage]);

        } catch (error) {
            console.error('Chat error:', error);
            toast.error(error.message || 'Failed to send message');

            // Add error message
            setMessages(prev => [...prev, {
                id: 'error-' + Date.now(),
                role: 'assistant',
                content: `Sorry, I encountered an error: ${error.message}. Please try again.`,
                timestamp: new Date().toISOString(),
                isError: true
            }]);
        } finally {
            setIsLoading(false);
            inputRef.current?.focus();
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className={`flex flex-col h-full bg-slate-900/95 backdrop-blur-xl ${className}`}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 flex-shrink-0">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-gradient-to-br from-orange-500/20 to-amber-500/20 flex items-center justify-center border border-orange-500/30">
                        <Bot className="h-5 w-5 text-orange-400" />
                    </div>
                    <div>
                        <h3 className="text-sm font-semibold text-white">AI Assistant</h3>
                        <p className="text-xs text-slate-400">Digital Ninja Builder</p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={clearChat}
                        className="text-slate-400 hover:text-red-400 hover:bg-red-500/10"
                        title="Clear chat"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                    {onClose && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onClose}
                            className="text-slate-400 hover:text-white"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            {/* Messages Area */}
            <ScrollArea className="flex-1 p-4" ref={scrollRef}>
                <div className="space-y-4">
                    {messages.map((msg) => (
                        <div
                            key={msg.id}
                            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                            <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                                {/* Message bubble */}
                                <div
                                    className={`rounded-2xl px-4 py-2.5 ${msg.role === 'user'
                                            ? 'bg-gradient-to-r from-orange-500 to-amber-500 text-white'
                                            : msg.isError
                                                ? 'bg-red-500/10 border border-red-500/30 text-red-200'
                                                : 'bg-slate-800/80 border border-slate-700/50 text-slate-100'
                                        } ${msg.role === 'user' ? 'animate-message-user' : 'animate-message-ai'}`}
                                >
                                    {/* User attachments indicator */}
                                    {msg.attachments && msg.attachments.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mb-2 pb-2 border-b border-white/20">
                                            {msg.attachments.map((att, i) => (
                                                <span key={i} className="text-xs bg-white/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                                                    <FileText className="h-3 w-3" />
                                                    {att.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                                </div>

                                {/* Timestamp */}
                                <div className={`text-[10px] text-slate-500 mt-1 ${msg.role === 'user' ? 'text-right' : 'text-left'} px-2`}>
                                    {formatTimestamp(msg.timestamp)}
                                </div>

                                {/* Plan Summary (for assistant messages) */}
                                {msg.role === 'assistant' && msg.planSummary && (
                                    <Collapsible
                                        open={expandedSummaries.has(msg.id)}
                                        onOpenChange={() => toggleSummary(msg.id)}
                                        className="mt-2"
                                    >
                                        <CollapsibleTrigger asChild>
                                            <button className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-orange-400 transition-colors px-2 py-1 rounded hover:bg-slate-800/50">
                                                <Sparkles className="h-3 w-3" />
                                                <span>Plan / Reasoning Summary</span>
                                                {expandedSummaries.has(msg.id) ? (
                                                    <ChevronUp className="h-3 w-3" />
                                                ) : (
                                                    <ChevronDown className="h-3 w-3" />
                                                )}
                                            </button>
                                        </CollapsibleTrigger>
                                        <CollapsibleContent>
                                            <div className="mt-1 p-3 rounded-lg bg-slate-800/50 border border-slate-700/30 text-xs text-slate-300 font-mono whitespace-pre-wrap">
                                                {msg.planSummary}
                                            </div>
                                        </CollapsibleContent>
                                    </Collapsible>
                                )}

                                {/* Attachments received indicator */}
                                {msg.attachmentsReceived && msg.attachmentsReceived.length > 0 && (
                                    <div className="mt-2 px-2">
                                        <div className="text-xs text-emerald-400 flex items-center gap-1 mb-1">
                                            <FileText className="h-3 w-3" />
                                            Files processed:
                                        </div>
                                        <div className="flex flex-wrap gap-1">
                                            {msg.attachmentsReceived.map((att, i) => (
                                                <span
                                                    key={i}
                                                    className={`text-[10px] px-2 py-0.5 rounded-full ${att.parsed
                                                            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                                            : 'bg-slate-700/50 text-slate-400 border border-slate-600/30'
                                                        }`}
                                                    title={`${att.mimetype} - ${formatFileSize(att.size)} - ${att.parsed ? 'Content parsed' : 'Metadata only'}`}
                                                >
                                                    {att.name} {att.parsed ? '✓' : '○'}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Avatar */}
                            <div className={`flex-shrink-0 ${msg.role === 'user' ? 'order-1 mr-2' : 'order-2 ml-2'}`}>
                                <div className={`h-7 w-7 rounded-full flex items-center justify-center ${msg.role === 'user'
                                        ? 'bg-slate-700'
                                        : 'bg-gradient-to-br from-orange-500/30 to-amber-500/30 border border-orange-500/30'
                                    }`}>
                                    {msg.role === 'user' ? (
                                        <User className="h-4 w-4 text-slate-300" />
                                    ) : (
                                        <Bot className="h-4 w-4 text-orange-400" />
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}

                    {/* Loading indicator */}
                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="flex items-center gap-2 ml-9">
                                <div className="bg-slate-800/80 border border-slate-700/50 rounded-2xl px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <Loader2 className="h-4 w-4 text-orange-400 animate-spin" />
                                        <span className="text-sm text-slate-400">Thinking...</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </ScrollArea>

            {/* Attachments preview */}
            {attachments.length > 0 && (
                <div className="px-4 py-2 border-t border-slate-700/30 bg-slate-800/30">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-slate-400">Attachments:</span>
                        {attachments.map((att, idx) => (
                            <div
                                key={idx}
                                className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-slate-700/50 border border-slate-600/50 group"
                            >
                                <FileText className="h-3.5 w-3.5 text-orange-400" />
                                <span className="text-xs text-slate-300 max-w-[100px] truncate">{att.name}</span>
                                <span className="text-[10px] text-slate-500">{formatFileSize(att.size)}</span>
                                <button
                                    onClick={() => removeAttachment(idx)}
                                    className="ml-1 p-0.5 hover:bg-red-500/20 rounded transition-colors"
                                >
                                    <X className="h-3 w-3 text-slate-400 hover:text-red-400" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Voice listening indicator */}
            {voice.isListening && (
                <div className="px-4 py-2 bg-red-500/10 border-t border-red-500/30">
                    <div className="flex items-center justify-center gap-2 text-red-400">
                        <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                        <span className="text-xs">Listening... Click mic to stop</span>
                    </div>
                </div>
            )}

            {/* Input area */}
            <div className="p-3 border-t border-slate-700/50 flex-shrink-0">
                <div className="flex items-center gap-2 bg-slate-800/60 rounded-xl border border-slate-700/50 p-2">
                    {/* Attach button */}
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={isLoading}
                        className="text-slate-400 hover:text-orange-400 hover:bg-slate-700/50 p-2"
                        title="Attach files"
                    >
                        <Paperclip className="h-5 w-5" />
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                        accept=".txt,.md,.json,.csv,.log,.xml,.html,.css,.js,.py,.yaml,.yml,text/*,application/json"
                    />

                    {/* Voice button */}
                    {voice.isSupported ? (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={voice.toggleListening}
                            disabled={isLoading}
                            className={`p-2 ${voice.isListening
                                    ? 'text-red-400 bg-red-500/20 hover:bg-red-500/30'
                                    : 'text-slate-400 hover:text-orange-400 hover:bg-slate-700/50'
                                }`}
                            title={voice.isListening ? 'Stop listening' : 'Start voice input'}
                        >
                            {voice.isListening ? (
                                <MicOff className="h-5 w-5" />
                            ) : (
                                <Mic className="h-5 w-5" />
                            )}
                        </Button>
                    ) : (
                        <Button
                            variant="ghost"
                            size="sm"
                            disabled
                            className="text-slate-600 p-2 cursor-not-allowed"
                            title="Voice input not supported in this browser"
                        >
                            <Mic className="h-5 w-5" />
                        </Button>
                    )}

                    {/* Text input */}
                    <Input
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type a message..."
                        disabled={isLoading}
                        className="flex-1 bg-transparent border-none focus-visible:ring-0 text-white placeholder-slate-500"
                    />

                    {/* Send button */}
                    <Button
                        onClick={sendMessage}
                        disabled={isLoading || (!inputValue.trim() && attachments.length === 0)}
                        className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-400 hover:to-amber-400 text-white shadow-lg shadow-orange-500/20 disabled:opacity-50"
                    >
                        {isLoading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Send className="h-4 w-4" />
                        )}
                    </Button>
                </div>

                {!voice.isSupported && (
                    <p className="text-[10px] text-slate-500 mt-1 text-center flex items-center justify-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Voice input not supported in this browser
                    </p>
                )}
            </div>
        </div>
    );
};

export default ChatPanel;
